import { useState, useEffect, useCallback } from 'react';
import { useUser, useSession } from '@clerk/clerk-react';

export type AuthStatus = 'authenticated' | 'inactive' | 'unauthenticated';

export interface AuthStatusState {
  status: AuthStatus;
  label: string;
  dotColor: string;
  textColor: string;
}

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

export function useAuthStatus(): AuthStatusState {
  const { isSignedIn, user } = useUser();
  const { session } = useSession();
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isInactive, setIsInactive] = useState(false);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setIsInactive(false);
  }, []);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const throttledUpdateActivity = throttle(updateActivity, 1000); // Throttle to once per second
    
    events.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity, true);
      });
    };
  }, [updateActivity]);

  // Check for inactivity
  useEffect(() => {
    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT && isSignedIn) {
        setIsInactive(true);
      }
    };

    const interval = setInterval(checkInactivity, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [lastActivity, isSignedIn]);

  // Determine status and styling
  if (!isSignedIn || !session) {
    return {
      status: 'unauthenticated',
      label: 'Not Authenticated',
      dotColor: 'bg-destructive',
      textColor: 'text-destructive'
    };
  }

  if (isInactive) {
    return {
      status: 'inactive',
      label: 'Session Inactive',
      dotColor: 'bg-warning',
      textColor: 'text-warning'
    };
  }

  return {
    status: 'authenticated',
    label: 'Authenticated',
    dotColor: 'bg-success',
    textColor: 'text-success'
  };
}

// Simple throttle utility
function throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
  let inThrottle: boolean;
  return ((...args: any[]) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }) as T;
}